import { useState, useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  getAnalyticsCardData,
  getAnalyticsContentData,
  getGraphData,
  getDashboardData
} from "../actions";
import store, { RootState } from "store";
import { FromUTCEpochToMonthDayString } from "../../../../utils";
import { getConstantCampaignEmailId, isDateOfToday, resetAllTabsFilter } from "../../utils";
import {
  ANALYTICS_FILTERS,
  ANALYTICS_DASHBOARD_GRAPH_DATA,
} from "constants/types";


const colors = ["rgba(255, 150, 45,1)", "rgb(139, 165, 230)", "rgb(211, 27, 27)", "rgb(24, 195, 81)"];

const FromUTCEpochToMonthDayHourString = (epoch: number) => {
  const date: Date = new Date(epoch);
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}  ${date.getUTCHours()}:00/${date.getUTCFullYear()}`;
};
const ToMonthDayLabels = (labels: Array<number>) => {
  return labels.map((epoch: any) => {
    return FromUTCEpochToMonthDayString(epoch);
  });
};

const ToHourlyLabels = (labels: number[]) => {
  return labels.map((epoch: any) => {
    return FromUTCEpochToMonthDayHourString(epoch);
  });
};

const formatXAxisLabels = (showHourly: boolean, labels: number[]) => {
  let xAxisLabels;
  if (showHourly) {
    xAxisLabels = ToHourlyLabels(labels);
  } else {
    xAxisLabels = ToMonthDayLabels(labels);
  }
  return xAxisLabels;
};

export const useGraphDataScaler = (gradients: any) => {
  const [isLastDateOfGraphToday, setIsLastDateOfGraphToday] = useState(false);
  const [plotData, setPlotData] = useState({
    pDatasets: [],
    xAxesDateLabels: [],
  });
  const [firstRenderDone, setFirstRenderDone] = useState(false);
  const [rawData, setRawData] = useState({});
  const [colorGradients, setColorGradients] = useState(gradients);

  const data = useSelector((state: RootState) => {
    return state.analyticsGraph.data;
  }, shallowEqual);
  const isFilterClicked = useSelector((state: RootState) => state.analyticsFilters.filterApplied);
  const filterReset = useSelector((state: RootState) => state.analyticsDashboard.filterReset);
  const selectedEmailIds = useSelector((state: RootState) => state.analyticsDashboard.selectedAssetsIds);
  const excludeAssetsIds = useSelector((state: RootState) => state.analyticsDashboard.excludeAssetsIds);
  const selectedCampaignIds = useSelector((state: RootState) => state.analyticsDashboard.selectedCampaignsIds);
  const excludeCampaignsIds = useSelector((state: RootState) => state.analyticsDashboard.excludeCampaignsIds);
  const selectedDatesObject = useSelector((state: RootState) => state.analyticsGraph.selectedDatesObject);
  const filterStartDate = useSelector((state: RootState) => state.analyticsGraph.startDate);
  const filterEndDate = useSelector((state: RootState) => state.analyticsGraph.endDate);
  // const getDefaultData = useSelector((state: RootState) => state.analyticsDashboard.getDefaultData);
  const isShareProfile = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isShareProfile); // check if share profile is clicked
  const isDailyIQ = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isDailyIQ);
  const isWeeklyAccountInsights = useSelector(
    (state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isWeeklyAccountInsights
  ); // check if share profile is clicked

  const loading = useSelector((state: RootState) => {
    return state.analyticsGraph.loading;
  }, shallowEqual);
  const AllCampaignsFilterCheck = useSelector(
    (state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns
  );

  const formatData = (data: any, gradients: any) => {
    let pDatasets: any = [];
    let xAxesDateLabels: any = [];
    let legendLabels: any = [];
    let deliveredEmails: any = [];

    const epochsMap = new Map();

    let utcEpochs: Array<number> = Array.from(
      new Set(
        data.campaigns_series?.reduce((accumulator: any, dataset: any) => {
          legendLabels.push(dataset.campaign_name);
          return [
            ...accumulator,
            ...dataset.events_data.map((dataPoint: any) => {
              return dataPoint[0];
            }),
          ];
        }, [])
      )
    );
    utcEpochs = utcEpochs?.sort((a: any, b: any) => a - b);

    const xAxesLabels: Array<number> = utcEpochs;
    setIsLastDateOfGraphToday(isDateOfToday(utcEpochs[utcEpochs.length - 1]));
    xAxesLabels?.forEach((epoch, index) => {
      epochsMap.set(epoch, index);
    });
    const yAxesDatasets: Array<any> = [];
    data.campaigns_series?.forEach((campaign: any) => {
      let clicks: Array<any> = new Array(xAxesLabels.length).fill(0, 0, xAxesLabels.length);
      let opens: Array<any> = new Array(xAxesLabels.length).fill(0, 0, xAxesLabels.length);
      let delivered: Array<any> = new Array(xAxesLabels.length).fill(0, 0, xAxesLabels.length);

      let startIndex = 99999999;

      campaign?.events_data?.forEach((datapoint: Array<number>) => {
        const epoch: number = datapoint[0];
        const ind: number = epochsMap.get(epoch);
        clicks[ind] = datapoint[1];
        opens[ind] = datapoint[2];
        delivered[ind] = datapoint[3];
        if (ind < startIndex) {
          startIndex = ind;
        }
      });

      clicks.fill(null, 0, startIndex);
      opens.fill(null, 0, startIndex);
      delivered.fill(null, 0, startIndex);

      startIndex = 99999999;

      yAxesDatasets.push({ name: campaign.campaign_name, clicks, opens });
      deliveredEmails.push({ name: campaign.campaign_name, delivered });
    });

    store.dispatch({
      type: ANALYTICS_DASHBOARD_GRAPH_DATA.SET_DELIVERED_EMAIL,
      payload: { delivered_emails: deliveredEmails },
    });

    let colorIndex = 0;
    pDatasets = yAxesDatasets?.reduce((accumulator, currentValue) => {
      accumulator.push({
        label: "Clicks " + currentValue.name,
        fill: "+1",
        backgroundColor: gradients[colorIndex],
        borderColor: colors[colorIndex],
        borderCapStyle: "butt",
        borderDashOffset: 0.0,
        borderJoinStyle: "round",
        pointBorderColor: colors[colorIndex],
        pointBackgroundColor: "white",
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointHitRadius: 10,
        data: currentValue.clicks,
        pointStyle: "rectRot",
        spanGaps: true,
        borderDash: [0.3, 3],
      });
      accumulator.push({
        label: "Opens " + currentValue.name,
        fill: false,
        borderColor: colors[colorIndex],
        borderCapStyle: "butt",
        borderDashOffset: 0.0,
        borderJoinStyle: "round",
        pointBorderColor: colors[colorIndex],
        pointBackgroundColor: colors[colorIndex],
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointHitRadius: 10,
        data: currentValue.opens,
        pointStyle: "triangle",
        spanGaps: true,
        borderDash: [0.3, 3],
      });
      colorIndex++;
      return accumulator;
    }, []);
    // showing time for the emails for 1 day and having more than 1 data points for the same day
    xAxesDateLabels = formatXAxisLabels(
      (selectedDatesObject.value === "day" || filterStartDate === filterEndDate) && xAxesLabels.length > 1,
      xAxesLabels
    );
    return {
      xAxesDateLabels,
      pDatasets,
      legendLabels,
    };
  };

  useEffect(() => {
    if (loading) {
      setPlotData({
        pDatasets: [],
        xAxesDateLabels: [],
      });
    }
  }, [loading]);

  useEffect(() => {
    if (data && Object.keys(data).length !== 0 && data.constructor === Object) {
      if (JSON.stringify(rawData) !== JSON.stringify(data)) {
        setRawData(data);
      }
    }
  }, [data]);

  useEffect(() => {
    if (loading) {
      setPlotData({
        pDatasets: [],
        xAxesDateLabels: [],
      });
    } else if (rawData && Object.keys(rawData).length !== 0 && colorGradients?.length > 0) {
      const formattedData = formatData(rawData, colorGradients);
      setPlotData(formattedData);
    }
  }, [rawData, colorGradients, loading]);

  useEffect(() => {
    if (isFilterClicked === true) {
      let analyticsGraph = store.getState().analyticsGraph;
      let { startDate, endDate } = analyticsGraph;
      let object: any = {
        allCampaigns: AllCampaignsFilterCheck,
        startDate: startDate,
        endDate: endDate,
      };
      if (selectedCampaignIds.length > 0 && selectedEmailIds.length > 0) {
        object["campaignIds"] = selectedCampaignIds;
        object["emailIds"] = selectedEmailIds;
      }

      // check if is share profile || daily IQ || weekly accounts insights
      if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
        let campaign_email_id = getConstantCampaignEmailId({
          isShareProfile,
          isDailyIQ,
          isWeeklyAccountInsights,
        });
        object["campaignIds"] = [campaign_email_id];
        object["emailIds"] = [campaign_email_id];
      }

      if (AllCampaignsFilterCheck) {
        object["excludeCampaignsIds"] = excludeCampaignsIds;
        object["excludeAssetsIds"] = excludeAssetsIds;
      }

      getGraphData(object);
      getAnalyticsCardData(object);
      getAnalyticsContentData(object);
      resetAllTabsFilter(true);
      store.dispatch({ type: ANALYTICS_FILTERS.RESET });
    }
  }, [isFilterClicked]);


  useEffect(() => {
    getDashboardData(firstRenderDone, setFirstRenderDone);
  }, [firstRenderDone]);

  return [plotData, isLastDateOfGraphToday, setColorGradients];
};
